import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
  type NotificationSeverity,
} from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";

type Filter = "all" | "unread";

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");

  const query = useInfiniteQuery({
    queryKey: ["notifications", "page", filter],
    queryFn: ({ pageParam }) =>
      listNotifications({
        unread_only: filter === "unread",
        cursor: pageParam ?? undefined,
        limit: 25,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const items: Notification[] = query.data?.pages.flatMap((p) => p.data) ?? [];
  const unreadCount = items.filter((n) => n.read_at === null).length;

  const handleClick = (n: Notification) => {
    if (n.read_at === null) markRead.mutate(n.id);
    if (n.action_url) navigate(n.action_url);
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        breadcrumbs={[{ label: "Notifications" }]}
        actions={
          unreadCount > 0 ? (
            <button
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="btn btn-light btn-sm"
            >
              <CheckCheck size={14} />
              Tout marquer lu
            </button>
          ) : undefined
        }
      />

      <div className="card mb-5">
        <div className="card-body flex items-center gap-1 py-3">
          <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
            Toutes
          </FilterButton>
          <FilterButton active={filter === "unread"} onClick={() => setFilter("unread")}>
            Non lues
          </FilterButton>
        </div>
      </div>

      <div className="card">
        {query.isLoading && (
          <div className="card-body text-center text-sm text-[#7e8299]">Chargement…</div>
        )}
        {!query.isLoading && items.length === 0 && (
          <div className="card-body text-center py-16">
            <Bell size={40} className="mx-auto text-[#cbd5e0] mb-3" />
            <div className="text-sm text-[#7e8299]">
              {filter === "unread"
                ? "Aucune notification non lue."
                : "Aucune notification pour l'instant."}
            </div>
          </div>
        )}
        {items.length > 0 && (
          <div>
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-5 py-4 border-b border-[#f5f8fa] hover:bg-[#f9fafb] transition-colors flex items-start gap-4 ${
                  n.read_at === null ? "bg-brand-50/30" : ""
                }`}
              >
                <div className="shrink-0 mt-0.5">
                  <SeverityIcon severity={n.severity} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <div
                      className={`text-sm text-[#181c32] ${
                        n.read_at === null ? "font-bold" : "font-medium"
                      }`}
                    >
                      {n.title}
                    </div>
                    <div
                      className="text-[11px] text-[#a1a5b7] shrink-0"
                      title={formatDate(n.created_at)}
                    >
                      {formatRelative(n.created_at)}
                    </div>
                  </div>
                  <div className="text-sm text-[#5e6278] leading-relaxed">{n.body}</div>
                  <div className="text-[11px] text-[#a1a5b7] mt-1.5 font-mono">
                    {n.type}
                  </div>
                </div>
                {n.read_at === null && (
                  <div className="w-2 h-2 rounded-full bg-brand-500 shrink-0 mt-2" />
                )}
              </button>
            ))}
          </div>
        )}
        {query.hasNextPage && (
          <div className="px-5 py-4 text-center border-t border-[#eff2f5]">
            <button
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              className="btn btn-light btn-sm"
            >
              {query.isFetchingNextPage ? "Chargement…" : "Charger plus"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active
          ? "bg-brand-50 text-brand-700"
          : "text-[#5e6278] hover:bg-[#f5f8fa]"
      }`}
    >
      {children}
    </button>
  );
}

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  const size = 22;
  switch (severity) {
    case "success":
      return <CheckCircle2 size={size} className="text-success-700" />;
    case "warning":
      return <AlertTriangle size={size} className="text-warning-700" />;
    case "danger":
      return <XCircle size={size} className="text-danger-700" />;
    case "info":
    default:
      return <Info size={size} className="text-info-700" />;
  }
}
